import { useState } from 'react';

import PropTypes from 'prop-types';

import chevronUpIcon from '@shared/assets/images/chevron_up_mobile_icon.svg';
import chevronRightIcon from '@shared/assets/images/chevron_rignt_mobile.svg';
import attachedIcon from '@shared/assets/images/attached_icon.svg';

import styles from './MobileAttachmentSection.module.scss';

/**
 * 모바일 첨부파일/참조문서/코멘트 섹션 컴포넌트
 * - 피그마 디자인: 접기/펼치기 기능
 * - 요약 및 비고: 줄바꿈으로 전체 내용 표시
 * - 첨부파일, 참조문서, 코멘트: 리스트 전체 표시 (스크롤 없음)
 */
function MobileAttachmentSection({
  summaryNote,
  attachments,
  referenceDocuments,
  comments,
  showCommentWriteButton,
  onFileClick,
  onReferenceDocClick,
  onCommentClick,
  onCommentWriteClick,
}) {
  const [isSummaryOpen, setIsSummaryOpen] = useState(true);
  const [isAttachmentsOpen, setIsAttachmentsOpen] = useState(true);
  const [isReferencesOpen, setIsReferencesOpen] = useState(true);
  const [isCommentsOpen, setIsCommentsOpen] = useState(true);

  const hasSummary = summaryNote && summaryNote.trim().length > 0;
  const hasAttachments = attachments && attachments.length > 0;
  const hasReferences = referenceDocuments && referenceDocuments.length > 0;
  const hasComments = comments && comments.length > 0;

  function toggleSummary() {
    setIsSummaryOpen(!isSummaryOpen);
  }

  function toggleAttachments() {
    setIsAttachmentsOpen(!isAttachmentsOpen);
  }

  function toggleReferences() {
    setIsReferencesOpen(!isReferencesOpen);
  }

  function toggleComments() {
    setIsCommentsOpen(!isCommentsOpen);
  }

  return (
    <div className={styles.container}>
      {/* 요약 및 비고 섹션 */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <button type="button" className={styles.titleButton} onClick={toggleSummary}>
            <span className={styles.sectionTitle}>요약 및 비고</span>
            <img
              src={chevronUpIcon}
              alt=""
              className={`${styles.chevron} ${isSummaryOpen ? styles.open : ''}`}
            />
          </button>
        </div>

        {isSummaryOpen && (
          <div className={styles.summaryContent}>
            {!hasSummary ? (
              <div className={styles.emptyMessage}>등록된 요약 및 비고가 없습니다.</div>
            ) : (
              <div className={styles.summaryText}>{summaryNote}</div>
            )}
          </div>
        )}
      </div>

      {/* 첨부 파일 섹션 */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <button type="button" className={styles.titleButton} onClick={toggleAttachments}>
            <span className={styles.sectionTitle}>첨부 파일</span>
            <img
              src={chevronUpIcon}
              alt=""
              className={`${styles.chevron} ${isAttachmentsOpen ? styles.open : ''}`}
            />
          </button>
        </div>

        {isAttachmentsOpen && (
          <div className={styles.fileList}>
            {!hasAttachments ? (
              <div className={styles.emptyMessage}>등록된 첨부파일이 없습니다.</div>
            ) : (
              attachments.map(function (file, index) {
                const fileName = file.name || file.fileName || '파일';

                return (
                  <button
                    key={`attachment-${file.idx || index}`}
                    type="button"
                    className={styles.fileItem}
                    onClick={function () {
                      if (onFileClick) {
                        // PC 버전과 동일하게 file.idx만 전달 (handleFileDownload가 idx를 기대함)
                        onFileClick(file.idx || file.id);
                      }
                    }}
                  >
                    {/* 파일 아이콘 */}
                    <img src={attachedIcon} alt="" className={styles.fileIcon} />
                    <span className={styles.fileName}>{fileName}</span>
                    <img src={chevronRightIcon} alt="" className={styles.arrowIcon} />
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* 참조 문서 섹션 */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <button type="button" className={styles.titleButton} onClick={toggleReferences}>
            <span className={styles.sectionTitle}>참조 문서</span>
            <img
              src={chevronUpIcon}
              alt=""
              className={`${styles.chevron} ${isReferencesOpen ? styles.open : ''}`}
            />
          </button>
        </div>

        {isReferencesOpen && (
          <div className={styles.referenceList}>
            {!hasReferences ? (
              <div className={styles.emptyMessage}>등록된 참조문서가 없습니다.</div>
            ) : (
              referenceDocuments.map(function (doc, index) {
                const docTitle = doc.title || doc.doc_title || '문서';
                const docAuthor = doc.name || doc.drafter || '';

                return (
                  <button
                    key={`reference-${doc.idx || index}`}
                    type="button"
                    className={styles.referenceItem}
                    onClick={function () {
                      if (onReferenceDocClick) {
                        onReferenceDocClick(doc);
                      }
                    }}
                  >
                    <div className={styles.referenceInfo}>
                      <span className={styles.referenceTitle}>{docTitle}</span>
                      {docAuthor && <span className={styles.referenceAuthor}>{docAuthor}</span>}
                    </div>
                    <img src={chevronRightIcon} alt="" className={styles.arrowIcon} />
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* 코멘트 섹션 */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <button type="button" className={styles.titleButton} onClick={toggleComments}>
            <span className={styles.sectionTitle}>코멘트</span>
            <img
              src={chevronUpIcon}
              alt=""
              className={`${styles.chevron} ${isCommentsOpen ? styles.open : ''}`}
            />
          </button>

          {/* 코멘트 작성 버튼 */}
          {showCommentWriteButton && (
            <button type="button" className={styles.writeButton} onClick={onCommentWriteClick}>
              작성
            </button>
          )}
        </div>

        {isCommentsOpen && (
          <div className={styles.commentList}>
            {!hasComments ? (
              <div className={styles.emptyMessage}>등록된 코멘트가 없습니다.</div>
            ) : (
              comments.map(function (comment, index) {
                const commentText = comment.comment || comment.content || '';
                const authorName = comment.name || '익명';

                return (
                  <button
                    key={`comment-${comment.idx || index}`}
                    type="button"
                    className={styles.commentItem}
                    onClick={function () {
                      if (onCommentClick) {
                        onCommentClick(comment);
                      }
                    }}
                  >
                    <div className={styles.commentInfo}>
                      <span className={styles.commentText}>{commentText}</span>
                      <span className={styles.commentAuthor}>{authorName}</span>
                    </div>
                    <img src={chevronRightIcon} alt="" className={styles.arrowIcon} />
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}

MobileAttachmentSection.propTypes = {
  summaryNote: PropTypes.string,
  attachments: PropTypes.array,
  referenceDocuments: PropTypes.array,
  comments: PropTypes.array,
  showCommentWriteButton: PropTypes.bool,
  onFileClick: PropTypes.func,
  onReferenceDocClick: PropTypes.func,
  onCommentClick: PropTypes.func,
  onCommentWriteClick: PropTypes.func,
};

MobileAttachmentSection.defaultProps = {
  summaryNote: '',
  attachments: [],
  referenceDocuments: [],
  comments: [],
  showCommentWriteButton: false,
  onFileClick: null,
  onReferenceDocClick: null,
  onCommentClick: null,
  onCommentWriteClick: null,
};

export default MobileAttachmentSection;
